import { Platform } from 'react-native';
import { ROUTES } from '@/common/constants/routes';
import { getDefaultLang } from '@/common/i18n';
import { MENHIR_ENDPOINTS } from '@/common/integrations/menhir/menhir.endpoints';
import { StorageService } from '@/common/integrations/storage/storage.service';
import { clearSessionData } from '@/common/utils/session';
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { router } from 'expo-router';

interface IRenewTokenResponse {
  data: {
    data: {
      access_token: string;
      refresh_token: string;
    };
  };
}

const BASE_URL = `${process.env.EXPO_PUBLIC_API_HOST || ''}/api`;

export const menhirClient = axios.create({
  baseURL: BASE_URL,
});

export const menhirClientVanilla = axios.create({
  baseURL: BASE_URL,
});

let pendingRenewRequest: Promise<IRenewTokenResponse> | null = null;
const refreshToken = async (error: AxiosError) => {
  const { config } = error;

  if (
    config?.url?.includes(MENHIR_ENDPOINTS.EMPLOYEE.SESSIONS.RENEW) ||
    config?.url?.includes('/sessions') ||
    config?.url?.includes('/registration')
  )
    return;

  const refreshToken = await StorageService.getRefreshToken();

  if (!pendingRenewRequest) {
    pendingRenewRequest = axios.post<null, IRenewTokenResponse>(
      `${BASE_URL}${MENHIR_ENDPOINTS.EMPLOYEE.SESSIONS.RENEW}`,
      null,
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
        timeout: 5000,
      }
    );
  }

  const { data } = await pendingRenewRequest;
  pendingRenewRequest = null;

  const {
    data: { access_token, refresh_token },
  } = data;

  await StorageService.updateTokens(access_token, refresh_token);
  return access_token;
};

export const requestInterceptor = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  const authToken = await StorageService.getAuthToken();

  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }

  //@TODO: How to get the version from the app?
  config.headers['x-coverflex-version'] = '1.64.1';
  config.headers['x-coverflex-channel'] =
    Platform.OS === 'web' ? 'web' : 'mobile';
  config.headers['x-coverflex-language'] =
    (await StorageService.getLanguage()) ?? getDefaultLang();

  return config;
};

export const responseSuccessInterceptor = (response: AxiosResponse) =>
  response.data;

export const responseErrorInterceptor = async (error: AxiosError<any>) => {
  const { response, config } = error;
  if (response && config) {
    if (response.status === 401) {
      try {
        const authToken = await refreshToken(error);
        if (authToken) {
          return menhirClient.request(config);
        }
      } catch {
        if (pendingRenewRequest) {
          clearSessionData();
          router.push({
            pathname: ROUTES.SIGNIN,
            params: { sessionExpired: 'true' },
          });
          pendingRenewRequest = null;
        }
      }
    }

    const { data } = response;

    return Promise.reject({
      statusCode: response?.status,
      errorCode: data.errors?.status,
      errors: data.errors ?? null,
    });
  }
};

menhirClient.interceptors.request.use(requestInterceptor);
menhirClient.interceptors.response.use(
  responseSuccessInterceptor,
  responseErrorInterceptor
);

menhirClientVanilla.interceptors.response.use(
  (response) => response,
  (error: AxiosError<any>) => {
    return Promise.reject({
      statusCode: error.response?.status,
      errorCode: error.response?.data.errors?.status,
      errors: error.response?.data.errors ?? null,
    });
  }
);
